import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'

import { Add, AutoFixHigh, Edit } from '@mui/icons-material'
import { Box, CircularProgress, IconButton, Menu, MenuItem } from '@mui/material'

import { Text } from '@trivie/ui-web/components/text'
import { color } from '@trivie/ui-web/theme'

import { DocumentViewer } from './components/document-viewer'
import { ContentGenerationTabs } from './content-generation-tabs'
import { EditResource } from '@trivie/ui-web/components/edit-resource'
import { ExpandMore } from '@trivie/ui-web/components/expand-button/expand-button'
import { GetBackgroundColorOfMimeType, ResourceHelper, useStores } from '@trivie/core'
import { ResourceApi } from '@trivie/core/src/services/api-objects'
import { ReactComponent as NullState } from '../../content-creation/null-states/null-resource-settings.svg'
import { Button } from '@trivie/ui-web/components/button'
import { ResourceIcon } from '@trivie/ui-web/components/resource-icon'
import { scrollToCitation } from '@trivie/core/src/utils/scrollToCitation'
import { QuizResourceSettings } from '../../content-creation/components/quiz-source-settings'

interface ResourcePanelProps {
  quiz?: any
  onboarding?: boolean
  style?: any
  selectedTab?: any
  setSelectedTab?: any
}

export const ResourcePanel = observer((props: ResourcePanelProps) => {
  const { selectedTab, setSelectedTab, onboarding, quiz, style } = props

  const { manageUserStore, resourceStore } = useStores()
  const { userProfile } = manageUserStore
  const { availableResources } = resourceStore

  const [showEditResource, setShowEditResource] = useState(false)
  const [resources, setResources] = useState<any[]>([])

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const wait = (ms) => new Promise((res) => setTimeout(res, ms))
  const poll = async (fn, attempts = 0) => {
    const MAX_ATTEMPTS = 15
    try {
      return await fn(quiz?.mercuryResource.resource_id)
    } catch (e) {
      if (attempts > MAX_ATTEMPTS) {
        // Max attempts reached with no success
        // return e
        await wait(30000)
        return poll(fn, attempts + 1)
      }
      await wait(2 ** attempts * 1000)
      return poll(fn, attempts + 1)
    }
  }

  const [fakeLoad, setFakeLoad] = useState(false)
  useEffect(() => {
    setFakeLoad(true)
    setTimeout(() => {
      setFakeLoad(false)
    }, 500)
  }, [quiz?.mercuryResource?.resource_id])

  useEffect(() => {
    if (
      ((quiz?.mercuryResource?.isVideo &&
        quiz?.mercuryResource?.transcoding_status === 'COMPLETE') ||
        quiz?.mercuryResource?.transcoding_status === 'SUBMITTED') &&
      quiz?.mercuryResource?.processing_status === 'not_started'
    ) {
      quiz?.setLoadingDocumentStatus('')
      processResource()
    }
  }, [quiz?.mercuryResource?.transcoding_status, quiz?.mercuryResource?.processing_status])

  useEffect(() => {
    if (quiz?.mercuryResource?.resource_id) {
      quiz?.getResourceStatus().then(() => {
        if (
          (quiz?.mercuryResource?.processing_status !== 'not_started' &&
            quiz?.mercuryResource?.processing_status !== 'failed') ||
          quiz?.mercuryResource?.transcoding_status === 'SUBMITTED'
        ) {
          quiz?.setLoadingDocumentStatus('')
          poll(quiz?.getResourceDocument)
        }
      })
    }
  }, [
    quiz?.mercuryResource,
    quiz?.mercuryResource?.transcoding_status,
    quiz?.mercuryResource?.processing_status,
    quiz?.mercuryResource?.resource_id,
  ])

  useEffect(() => {
    if (quiz?.loadingDocumentStatus === 'Failed') {
      quiz?.setMercuryResource(undefined)
    }
  }, [quiz?.loadingDocumentStatus])

  const [progress, setProgress] = React.useState(0)

  useEffect(() => {
    let timer
    if (quiz?.loadingDocument) {
      timer = setInterval(() => {
        if (progress !== 100) {
          setProgress((prevProgress) =>
            prevProgress >= 100 ? 100 : prevProgress + Math.floor(Math.random() * 10 + 1),
          )
        }
      }, 2200)
    }
    return () => {
      setProgress(0)
      clearInterval(timer)
    }
  }, [quiz?.loadingDocument])

  useEffect(() => {
    let r = [] as any
    if (quiz?.resources?.length) {
      r = [...quiz.resources]
    }
    const available = ResourceHelper.fetchedResourcesToResources(availableResources)
    available?.map((avail) => {
      quiz?.removingResources?.map((rid) => {
        if (rid === avail.resource_id) {
          r.push(avail)
        }
      })
    })
    setResources(r)
  }, [quiz?.resources, availableResources, quiz?.addingResources, quiz?.removingResources])

  const processResource = async () => {
    const resource_id = quiz?.mercuryResource.resource_id
    ResourceApi.processResource(resource_id).then(async () => {
      ResourceApi.getSourceDetails(quiz?.mercuryResource.source_id).then((resp) => {
        // trigger polling by unattaching / reattaching resource with updated processing status
        quiz.setMercuryResource(undefined)
        quiz.setMercuryResource(resp.data)
      })
    })
  }

  const getExtension = (resource) => {
    if (resource?.url || resource?.extension === null) {
      return 'url'
    } else {
      return resource.mime_type
    }
  }

  if (!quiz) {
    return null
  }

  return !userProfile?.workspace_settings?.mercury_enabled ? (
    <QuizResourceSettings quiz={quiz} />
  ) : (
    <div style={{ ...ROOT, ...style }}>
      <div style={{ ...CONTAINER, overflow: 'hidden' }}>
        <div
          style={{
            justifyContent: 'center',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            marginBottom: onboarding ? 24 : 0,
            position: 'relative',
            top: -6,
          }}
        >
          {quiz?.mercuryResource && (
            <div
              style={{
                alignItems: 'center',
                flexDirection: 'row',
                padding: 6,
                borderRadius: 10,
                display: 'flex',
                transform: `scale(0.6)`,
                backgroundColor: GetBackgroundColorOfMimeType(getExtension(quiz?.mercuryResource)),
              }}
            >
              <ResourceIcon extension={getExtension(quiz?.mercuryResource)} white={true} />
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Text
              style={{
                marginLeft: 8,
                marginRight: 8,
                maxWidth: 250,
                display: 'block',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
              variant="body1Emphasized"
              text={quiz?.mercuryResource ? quiz?.mercuryResource.title : 'No Resource Selected'}
            />
          </div>
          {!onboarding && (
            <ExpandMore
              disabled={quiz?.loadingDocument}
              style={{ marginLeft: 0 }}
              expand={open}
              onClick={handleClick}
            />
          )}
          <Menu
            style={{ paddingTop: 8, paddingBottom: 8 }}
            PaperProps={{ style: { width: '256px', transform: 'translateX(22px)' } }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
          >
            <MenuItem
              disabled={quiz?.loadingDocument}
              style={{ ...MENU_ITEM, borderBottom: `1px solid ${color.shade20}` }}
              onClick={() => {
                setShowEditResource(true)
                handleClose()
              }}
            >
              <>
                <Edit style={{ fontSize: 16 }} />
                <Text text={'Edit Source'} style={{ marginLeft: 8 }} variant="body2Medium" />
              </>
            </MenuItem>
            <MenuItem disabled style={{ ...MENU_ITEM, opacity: 1.0 }}>
              <Text
                text={'Quiz Sources'}
                variant="body2Emphasized"
                style={{ color: color.shade50 }}
              />
            </MenuItem>
            {resources?.map((r) => (
              <MenuItem
                key={`resource-${r.source_id}`}
                disabled={quiz?.loadingDocument}
                style={{ ...MENU_ITEM, paddingLeft: 6 }}
                onClick={() => {
                  if (quiz?.mercuryResource?.source_id !== r?.source_id) {
                    ResourceApi.getSourceDetails(r?.source_id).then((resp) => {
                      // trigger polling by unattaching / reattaching resource with updated processing status
                      quiz.setMercuryResource(undefined)
                      quiz.setMercuryResource(resp.data)
                      setTimeout(() => {
                        scrollToCitation(quiz?.selectedQuestion?.generated_question_id)
                      }, 300)
                    })
                  }
                  handleClose()
                }}
              >
                <>
                  <div
                    style={{
                      alignItems: 'center',
                      flexDirection: 'row',
                      padding: 6,
                      borderRadius: 10,
                      display: 'flex',
                      transform: `scale(0.6)`,
                      backgroundColor: GetBackgroundColorOfMimeType(getExtension(r)),
                    }}
                  >
                    <ResourceIcon extension={getExtension(r)} white={true} />
                  </div>
                  <Text
                    style={{
                      marginLeft: 8,
                      maxWidth: 230,
                      display: 'block',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                    }}
                    variant="body2Medium"
                  >{`${r?.title}`}</Text>
                </>
              </MenuItem>
            ))}
            <MenuItem
              disabled={quiz?.loadingDocument}
              style={MENU_ITEM}
              onClick={() => {
                quiz.setResourceDocument(undefined)
                quiz.setMercuryResource(undefined)
                handleClose()
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <Add style={{ fontSize: 16 }} />
                <Text style={{ marginLeft: 8 }} variant="body2Medium" text="Add Source" />
              </div>
            </MenuItem>
          </Menu>
        </div>
        <div
          style={{
            display: quiz?.mercuryResource ? 'none' : 'block',
            paddingRight: 24,
            paddingLeft: 24,
            marginTop: 20,
          }}
        >
          <Text
            variant="body1"
            text="Add or Select a Source"
            style={{ color: color.shade70, marginBottom: 24, fontWeight: 600, fontSize: 18 }}
          />
          {/* <Text
            variant="body1"
            text="The questions you create will be generated from the source you select."
            style={{
              color: color.shade50,
              // @ts-ignore
              textWrap: 'wrap',
            }}
          /> */}
        </div>
        <ContentGenerationTabs
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          quiz={quiz}
        />
        {quiz.mercuryResource &&
          (quiz?.loadingDocument || fakeLoad ? (
            <>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  flex: 1,
                  marginBottom: 20,
                  padding: 20,
                }}
              >
                <Text
                  text={`Preparing "${quiz?.mercuryResource?.title}" for generation.`}
                  variant="labelEmphasized"
                  style={{
                    alignSelf: 'flex-end',
                    color: color.shade70,
                    textAlign: 'center',
                    //@ts-ignore
                    textWrap: 'wrap',
                  }}
                />
              </div>
              <div style={LOADER}>
                <CircularProgress
                  variant="determinate"
                  sx={{
                    color: `${color.purple}20`,
                  }}
                  size={200}
                  thickness={4}
                  {...props}
                  value={100}
                />
                <CircularProgress
                  thickness={4}
                  size={200}
                  style={{
                    position: 'absolute',
                    // animationDuration: '6s',
                    strokeLinecap: 'round',
                    color: color.purple,
                  }}
                  variant="determinate"
                  value={progress}
                />
                <IconButton
                  disabled
                  sx={{
                    position: 'absolute',
                    animation: 'spin 6s linear infinite',
                    '@keyframes spin': {
                      '0%': {
                        transform: 'rotate(-360deg)',
                      },
                      '100%': {
                        transform: 'rotate(0deg)',
                      },
                    },
                  }}
                >
                  <svg
                    style={{
                      width: '48px',
                      height: '48px',
                      transform: 'rotate(-30deg)',
                    }}
                    xmlns="http://www.w3.org/2000/svg"
                    width="66"
                    height="66"
                    viewBox="0 0 66 66"
                    fill="none"
                  >
                    <g clipPath="url(#clip0_532_28389)">
                      <path
                        d="M32.0117 36.6881L26.2467 37.3029C26.0691 37.3285 25.9386 37.2692 25.8552 37.1248C25.7719 36.9805 25.7857 36.8378 25.8967 36.6967L29.2867 31.9683L28.6718 26.2033C28.6462 26.0256 28.7056 25.8951 28.8499 25.8118C28.9943 25.7285 29.137 25.7423 29.2781 25.8533L34.0065 29.2433L39.7965 28.6717C39.9741 28.6461 40.1046 28.7055 40.188 28.8498C40.2713 28.9942 40.2575 29.1369 40.1465 29.2779L36.7315 33.9631L37.3031 39.753C37.3287 39.9307 37.2693 40.0612 37.1249 40.1445C36.9806 40.2279 36.8379 40.214 36.6968 40.103L32.0117 36.6881ZM43.9999 52.0524C40.7668 53.919 37.0991 54.7664 32.9969 54.5945C28.8947 54.4226 25.2692 53.6097 22.1205 52.156L24.7955 56.7892C25.0121 57.1645 25.0686 57.5457 24.9649 57.9327C24.8612 58.3198 24.6217 58.6216 24.2464 58.8383C23.8711 59.0549 23.49 59.1114 23.1029 59.0077C22.7159 58.904 22.4141 58.6645 22.1974 58.2892L17.6974 50.495C17.4807 50.1197 17.4242 49.7386 17.5279 49.3515C17.6317 48.9645 17.8711 48.6626 18.2464 48.446L26.0407 43.946C26.4159 43.7293 26.7971 43.6728 27.1841 43.7765C27.5712 43.8802 27.873 44.1197 28.0897 44.495C28.3064 44.8703 28.3628 45.2515 28.2591 45.6385C28.1554 46.0255 27.9159 46.3274 27.5407 46.5441L22.9074 49.2191C25.663 50.5919 28.9173 51.3784 32.6703 51.5788C36.4232 51.7791 39.6998 51.071 42.4999 49.4543C46.8301 46.9543 49.7195 43.3423 51.1683 38.6184C52.6171 33.8944 52.2897 29.3107 50.1861 24.8672C49.9983 24.4752 49.9521 24.0785 50.0475 23.677C50.1429 23.2756 50.3782 22.9665 50.7535 22.7498C51.0999 22.5498 51.4738 22.4975 51.8753 22.5929C52.2768 22.6883 52.5631 22.9175 52.7342 23.2806C53.9943 25.8632 54.7226 28.5412 54.9189 31.3146C55.1152 34.088 54.8243 36.7674 54.0461 39.3529C53.2679 41.9384 52.0269 44.3388 50.323 46.5542C48.6191 48.7697 46.5114 50.6024 43.9999 52.0524ZM23.4999 16.5453C19.1698 19.0453 16.2804 22.6573 14.8316 27.3813C13.3828 32.1052 13.7102 36.689 15.8138 41.1325C16.0016 41.5244 16.0478 41.9211 15.9524 42.3226C15.857 42.7241 15.6217 43.0332 15.2464 43.2498C14.9 43.4498 14.5261 43.5021 14.1246 43.4068C13.7231 43.3114 13.4368 43.0822 13.2657 42.7191C12.0056 40.1365 11.2773 37.4585 11.081 34.6851C10.8847 31.9117 11.1756 29.2322 11.9538 26.6468C12.7319 24.0613 13.973 21.6608 15.6769 19.4454C17.3808 17.23 19.4885 15.3973 21.9999 13.9473C25.2331 12.0806 28.9008 11.2332 33.003 11.4051C37.1052 11.5771 40.7307 12.3899 43.8794 13.8436L41.2044 9.21041C40.9878 8.83513 40.9313 8.45397 41.035 8.06693C41.1387 7.67989 41.3782 7.37803 41.7535 7.16137C42.1287 6.9447 42.5099 6.88822 42.8969 6.99193C43.284 7.09564 43.5858 7.33513 43.8025 7.71041L48.3025 15.5046C48.5192 15.8799 48.5757 16.2611 48.4719 16.6481C48.3682 17.0352 48.1287 17.337 47.7535 17.5537L39.9592 22.0537C39.584 22.2703 39.2028 22.3268 38.8158 22.2231C38.4287 22.1194 38.1269 21.8799 37.9102 21.5046C37.6935 21.1294 37.6371 20.7482 37.7408 20.3612C37.8445 19.9741 38.084 19.6723 38.4592 19.4556L43.0925 16.7806C40.3369 15.4078 37.0898 14.6171 33.3513 14.4084C29.6127 14.1997 26.329 14.912 23.4999 16.5453Z"
                        fill="#1B59F8"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_532_28389">
                        <rect
                          width="48"
                          height="48"
                          fill="white"
                          transform="translate(0.215332 24.2153) rotate(-30)"
                        />
                      </clipPath>
                    </defs>
                  </svg>
                </IconButton>
              </div>
              <div
                style={{
                  marginTop: 20,
                  padding: 20,
                  display: 'flex',
                  justifyContent: 'center',
                  flex: 1,
                }}
              >
                <Text
                  className={quiz?.loadingDocumentStatus ? 'loading-ellipsis' : ''}
                  text={quiz?.loadingDocumentStatus}
                  variant="titleMedium"
                  style={{
                    fontSize: 15,
                    textAlign: 'center',
                    fontStyle: 'italic',
                    color: color.shade70,
                  }}
                />
              </div>
            </>
          ) : quiz?.mercuryResource?.processing_status === 'processed' ||
            quiz?.mercuryResource?.processing_status.includes('processed') ? (
            <DocumentViewer onboarding={onboarding} quiz={quiz} />
          ) : (
            <div style={NULL}>
              <div
                style={{
                  textAlign: 'center',
                  //@ts-ignore
                  textWrap: 'wrap',
                }}
              >
                <Text
                  text={`${quiz?.mercuryResource.title} is attached to this quiz.`}
                  variant="titleEmphasized"
                />
              </div>
              <NullState style={{ alignSelf: 'center' }} />
              <Button
                color="secondary"
                text="Analyze Document"
                startIcon={<AutoFixHigh />}
                onClick={processResource}
              />
            </div>
          ))}
      </div>
      {showEditResource && (
        <EditResource
          quiz={quiz}
          open={showEditResource}
          resource={quiz.mercuryResource}
          onClose={() => setShowEditResource(false)}
        />
      )}
    </div>
  )
})

const CONTAINER: React.CSSProperties = {
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  paddingTop: 16,
}
const ROOT: React.CSSProperties = {
  position: 'relative',
  display: 'flex',
  background: 'rgb(250,250,250)',
  flexDirection: 'column',
  alignSelf: 'center',
  height: '100%',
  flexGrow: 1,
  flex: 1,
  width: '100%',
  overflow: 'hidden',
}
const LOADER: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
}

const MENU_ITEM: React.CSSProperties = {
  height: 32,
  paddingTop: 8,
  paddingBottom: 8,
  paddingLeft: 12,
  paddingRight: 12,
  opacity: '1px !important',
}
const NULL: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  flex: 1,
  alignItems: 'center',
  gap: 24,
  paddingLeft: 24,
  paddingRight: 24,
  position: 'relative',
}
